import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, AlertCircle, ChefHat } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 8;

const Success = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | expired | error | timeout
  const [details, setDetails] = useState(null);
  const { clear } = useCart();
  const clearedRef = useRef(false);

  useEffect(() => {
    const windcaveResult = searchParams.get("result");
    if (!windcaveResult) {
      setState("error");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/windcave/process?result=${encodeURIComponent(windcaveResult)}`);
        if (cancelled) return;
        setDetails(data);
        if (data.payment_status === "paid") {
          setState("paid");
          if (!clearedRef.current) {
            clearedRef.current = true;
            clear();
          }
        } else {
          setState("expired");
        }
      } catch (e) {
        if (!cancelled) setState("error");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <main data-testid="page-success" className="max-w-3xl mx-auto px-6 lg:px-10 py-24 text-center">
      <div className="glass rounded-3xl p-12 border border-white/10">
        {state === "checking" && (
          <div data-testid="success-checking" className="flex flex-col items-center gap-4">
            <Loader2 className="w-12 h-12 animate-spin text-[#00b4ff]" />
            <h1 className="font-script text-5xl text-white">Confirming payment…</h1>
            <p className="text-gray-400">Hang tight — this only takes a moment.</p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="success-paid" className="flex flex-col items-center gap-4">
            <CheckCircle2 className="w-16 h-16 text-[#00b4ff]" />
            <h1 className="font-script text-6xl neon-gradient-text">Thank you!</h1>
            <p className="text-gray-300 max-w-md">
              {details?.fulfillment_type === "pickup" ? (
                <>Your order is confirmed. Check your email for a receipt — we'll have it ready for pickup{details?.pickup_day ? ` on ${details.pickup_day}` : ""}.</>
              ) : (
                <>Your order is confirmed. We'll have it ready shortly — check your email for a receipt, your order will be shipped within 1 business day (Monday-Friday).</>
              )}
            </p>
            {details?.order_notes && (
              <div
                data-testid="success-order-notes"
                className="mt-3 w-full max-w-md text-left bg-white/5 border border-white/10 rounded-2xl px-5 py-4"
              >
                <p className="text-[10px] uppercase tracking-[0.22em] text-gray-500 mb-1.5">Your note with this order</p>
                <p className="text-sm text-gray-200 italic leading-relaxed">"{details.order_notes}"</p>
              </div>
            )}
            {details && (
              <p data-testid="success-amount" className="text-sm text-gray-400 mt-2">
                Total charged: <span className="text-white font-semibold">${details.amount_total.toFixed(2)} {details.currency.toUpperCase()}</span>
              </p>
            )}
            {details?.order_status_badge && (
              <div
                data-testid="success-status-badge"
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#ff1a8c]/50 bg-[#ff1a8c]/10 text-[#ff1a8c] text-xs uppercase tracking-[0.22em] font-semibold shadow-[0_0_24px_rgba(255,26,140,0.25)]"
              >
                <ChefHat className="w-4 h-4" />
                <span>{details.order_status_badge}</span>
              </div>
            )}
            <div className="flex flex-wrap gap-4 justify-center mt-6">
              <Link to="/shop" data-testid="success-continue-shopping" className="neon-btn neon-btn--blue">Keep shopping</Link>
              <Link to="/" data-testid="success-back-home" className="neon-btn">Back home</Link>
            </div>
          </div>
        )}

        {state === "expired" && (
          <div data-testid="success-expired" className="flex flex-col items-center gap-4">
            <XCircle className="w-14 h-14 text-[#ff1a8c]" />
            <h1 className="font-script text-5xl text-white">Session expired</h1>
            <p className="text-gray-400">Your checkout session timed out. No charge was made.</p>
            <Link to="/shop" className="neon-btn mt-4">Try again</Link>
          </div>
        )}

        {state === "timeout" && (
          <div data-testid="success-timeout" className="flex flex-col items-center gap-4">
            <AlertCircle className="w-14 h-14 text-[#ff1a8c]" />
            <h1 className="font-script text-5xl text-white">Still processing…</h1>
            <p className="text-gray-400">
              We couldn't confirm your payment in time. Check your email for a receipt, or contact us if you don't hear back.
            </p>
            <Link to="/contact" className="neon-btn mt-4">Contact us</Link>
          </div>
        )}

        {state === "error" && (
          <div data-testid="success-error" className="flex flex-col items-center gap-4">
            <XCircle className="w-14 h-14 text-[#ff1a8c]" />
            <h1 className="font-script text-5xl text-white">Something went wrong</h1>
            <p className="text-gray-400">We couldn't verify your payment. If you were charged, contact us and we'll sort it out.</p>
            <Link to="/contact" className="neon-btn mt-4">Contact us</Link>
          </div>
        )}
      </div>
    </main>
  );
};

export default Success;
